import { type ReactNode } from 'react';
import tw, { styled } from 'twin.macro';
import { ToastContainer } from 'react-toastify';
import renderBlock from '@/utils/renderBlock';
import useAutomaticDownload from '@/hooks/useAutomaticDownload';
import usePageViewEvent from '@/hooks/usePageViewEvent';
import { graphql } from 'gatsby';

// Example code for Container before getting
// the max-width and/or right proportion from the design
const Container = styled.div`
  ${tw`w-full`}
`;

const StyledMain = styled.main<{
  hasFooter: boolean;
  hasHeader: boolean;
  isHeaderSticky: boolean;
}>`
  ${({ isHeaderSticky }) => !isHeaderSticky && tw`pt-0 lg:pt-0`}
  ${({ hasHeader, hasFooter }) => !hasHeader && !hasFooter && tw`bg-neutrals-n4 lg:min-h-[100vh]`}
  ${tw`bg-neutrals-n1`}
`;

type LayoutProps = {
  children?: ReactNode;
  elements: Queries.PAGE_LAYOUTFragment & { [key: string]: any };
  pageContext?: any;
  data?: any;
};

const Layout = ({
  children,
  elements: {
    page_layout__footer,
    page_layout__header,
    page_layout__header_ctas,
    page_seo_snippet__meta_keywords,
    ...elements
  },
  pageContext,
  data,
}: LayoutProps) => {
  const { automatic_download_asset, automatic_download_asset_url, building_blocks, creation_date } =
    elements;

  const isHeaderSticky = !building_blocks.value.some(
    (block: any) => block.system.type === 'secondary_navigation'
  );

  useAutomaticDownload(automatic_download_asset?.value?.url || automatic_download_asset_url?.value);
  usePageViewEvent({
    blogTags:
      (page_seo_snippet__meta_keywords?.value || []).filter(
        (
          keyword: Queries.BLOG_TAGFragment | Queries.META_KEYWORDFragment
        ): keyword is Queries.BLOG_TAGFragment => keyword.__typename === 'kontent_item_blog_tag'
      ) ?? [],
  });

  return (
    <>
      <ToastContainer position="top-center" autoClose={6000} theme="colored" icon={false} />
      <StyledMain
        hasFooter={page_layout__footer?.value !== 'hidden'}
        hasHeader={page_layout__header?.value !== 'hidden'}
        isHeaderSticky={isHeaderSticky}
      >
        <Container>
          {building_blocks.value.map((block: any) =>
            renderBlock({
              block,
              building_blocks,
              creation_date,
              data,
              footerProps: {
                layout: page_layout__footer,
              },
              headerProps: {
                layout: page_layout__header,
                isSticky: isHeaderSticky,
                hasCTAs: page_layout__header_ctas?.value !== 'hide',
              },
              metaKeywords: page_seo_snippet__meta_keywords,
              pageContext,
            })
          )}
          {children}
        </Container>
      </StyledMain>
    </>
  );
};

export const fragment = graphql`
  fragment PAGE_LAYOUT on kontent_snippet_page_layout {
    page_layout__header {
      value
    }
    page_layout__footer {
      value
    }
    page_layout__header_ctas {
      value
    }
  }
`;

export default Layout;
